import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { FilterRecord, FilterParams } from 'src/types';
import { AppRoutes } from 'src/lib/routes';
import humps from 'humps';
import { isEqual } from 'lodash-es';
import { ParsedUrlQuery } from 'querystring';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';

export const filterablePaths = [
  '/cities/[id]',
  '/cities/[id]/tags/[tagId]',
  '/countries/[id]',
  '/countries/[id]/tags/[tagId]',
  '/collections/[id]',
  '/collections/[id]/[cateId]',
  '/[countryCode]/[citySlug]/[id]',
  '/[countryCode]/[citySlug]/[id]/[cateId]',
  '/search'
];

export const filterKeys: (keyof FilterParams)[] = [
  'tab',
  'sortBy',
  'kind',
  'activityType',
  'time',
  'locationIds',
  'cityIds',
  'suitableFor',
  'prices',
  'masterPrices',
  'childAge',
  'durations',
  'tripType',
  'cityId',
  'countryId',
  'specificId',
  'badge',
  'topicTagIds'
];

export const defaultValue: FilterParams = {
  sortBy: 'recommended'
}

export function getFilterParamsFromReqQuery(reqQuery: ParsedUrlQuery) {
  return filterKeys.reduce((memo, key) => {
    const decamelizedKey = humps.decamelize(key);
    let aryValue = reqQuery[`${decamelizedKey}[]`];
    aryValue = typeof aryValue === 'string' ? [aryValue] : aryValue;
    memo[key] = aryValue || reqQuery[decamelizedKey] || defaultValue[key];
    return memo;
  }, {} as { [k: string]: any })
}

export const FilterContext = React.createContext<{
  filter: () => void;
  filterParams: FilterParams;
  setFilterParams: React.Dispatch<React.SetStateAction<FilterParams>>;
  filterRecord: FilterRecord | undefined;
  setFilterRecord: React.Dispatch<React.SetStateAction<FilterRecord | undefined>>;
  setPrevFilterParams: React.Dispatch<React.SetStateAction<FilterParams>>;
  shouldCloseFilterItem: number;
  setShouldCloseFilterItem: React.Dispatch<React.SetStateAction<number>>;
  setForceFilter: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  filter: () => {},
  filterParams: {},
  setFilterParams: () => {},
  filterRecord: undefined,
  setFilterRecord: () => {},
  setPrevFilterParams: () => {},
  shouldCloseFilterItem: 0,
  setShouldCloseFilterItem: () => {},
  setForceFilter: () => {},
});

const FilterProvider: React.ReactFCWithChildren = ({ children }) => {
  const router = useRouter();
  const [shouldCloseFilterItem, setShouldCloseFilterItem] = useState<number>(0);
  const [params, setParams] = useState<FilterParams>(defaultValue);
  const [forceFilter, setForceFilter] = useState(false)
  const [prevFilterParams, setPrevFilterParams] = useState<FilterParams>(defaultValue);
  const [record, setRecord] = useState<FilterRecord>();
  const currentCurrency = useCurrentCurrency()

  const filter = () => {
    if (!record) return;
    const isSame = isEqual(params, prevFilterParams);
    if (isSame && !forceFilter) return;
    setPrevFilterParams(params);

    const onChangeEvent = new CustomEvent('before_filter')
    window.document.dispatchEvent(onChangeEvent)

    router.push(
      AppRoutes.searchRoute(
        {},
        {
          ...params,
          filter_currency: currentCurrency,
          recordId: record.id,
          recordType: record.type,
          recordName: record.name,
          prevPath: record.prevPath,
        }
      ).toUrl(),
      undefined,
      { scroll: false }
    );
  };

  useEffect(() => {
    if (record?.id || record?.type === 'Fts') {
      const params = getFilterParamsFromReqQuery(router.query);
      setParams(params);
      setPrevFilterParams(params);
    }
  }, [record?.id, record?.type, router.query]);

  useEffect(() => {
    const hideFilter = () => {
      if (!filterablePaths.includes(router.pathname) || router.pathname === '/') {
        setRecord(undefined);
      }
    };

    const removeForceFilter = () => {
      setForceFilter(false)
    }

    router.events.on('routeChangeComplete', hideFilter);
    router.events.on('routeChangeComplete', removeForceFilter);

    return () => {
      router.events.off('routeChangeComplete', hideFilter);
      router.events.off('routeChangeComplete', removeForceFilter);
    };
  }, [router.pathname]);

  return (
    <FilterContext.Provider
      value={{
        filter,
        filterParams: params,
        setFilterParams: setParams,
        filterRecord: record,
        setFilterRecord: setRecord,
        setPrevFilterParams,
        shouldCloseFilterItem,
        setShouldCloseFilterItem,
        setForceFilter
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
